import { useState, useEffect, useRef, useCallback } from 'react';
import { APIClient } from '../../helpers/api_helper';

const api = new APIClient();

const REPORT_INTERVAL_MS = 20000; // heartbeat every 20s to keep TTL alive
const POLL_INTERVAL_MS = 2000;    // poll for others every 2s

const useProjectPresence = ({ projectId, isActive, isEditing, lastLoadedAt }) => {
  const [presenceData, setPresenceData] = useState([]);
  const [hasExternalSave, setHasExternalSave] = useState(false);
  const [lastSavedBy, setLastSavedBy] = useState(null);

  const isEditingRef = useRef(isEditing);
  const lastLoadedAtRef = useRef(lastLoadedAt);

  useEffect(() => { isEditingRef.current = isEditing; }, [isEditing]);
  useEffect(() => {
    lastLoadedAtRef.current = lastLoadedAt;
    // Clear the external-save flag when fresh data is loaded
    if (lastLoadedAt !== null) {
      setHasExternalSave(false);
      setLastSavedBy(null);
    }
  }, [lastLoadedAt]);

  const reportStatus = useCallback((status) => {
    if (!projectId) return;
    api.post(`/api/projects/${projectId}/report_presence/`, { status }, { successMsg: null })
      .catch(() => {});
  }, [projectId]);

  const fetchPresence = useCallback(() => {
    if (!projectId) return;
    api.get(`/api/projects/${projectId}/get_presence/`)
      .then(data => {
        setPresenceData(data.presence || []);

        const ls = data.last_save;
        const loadedAt = lastLoadedAtRef.current;
        if (ls && loadedAt && ls.ts > loadedAt) {
          setHasExternalSave(true);
          setLastSavedBy(ls.name);
        }
      })
      .catch(() => {});
  }, [projectId]);

  // Report immediately whenever editing state changes
  useEffect(() => {
    isEditingRef.current = isEditing;
    if (isActive && projectId) {
      reportStatus(isEditing ? 'editing' : 'viewing');
    }
  }, [isEditing, isActive, projectId, reportStatus]);

  // Set up intervals when on budget tab; clean up on leave
  useEffect(() => {
    if (!isActive || !projectId) {
      setPresenceData([]);
      setHasExternalSave(false);
      setLastSavedBy(null);
      return;
    }

    reportStatus(isEditingRef.current ? 'editing' : 'viewing');
    fetchPresence();

    const reportInterval = setInterval(() => {
      reportStatus(isEditingRef.current ? 'editing' : 'viewing');
    }, REPORT_INTERVAL_MS);

    const pollInterval = setInterval(fetchPresence, POLL_INTERVAL_MS);

    return () => {
      clearInterval(reportInterval);
      clearInterval(pollInterval);
      reportStatus('left');
    };
  }, [isActive, projectId, reportStatus, fetchPresence]);

  return { presenceData, hasExternalSave, lastSavedBy };
};

export default useProjectPresence;
